import { IonContent, IonPage, useIonViewWillEnter } from "@ionic/react";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CommonHeader from "../../../CommonHeader";
import secondMedicalOpinionImg from "../../../assets/images/second-medical-opinion.png";
import {
  getAllPlans,
  getPlanService,
  getProvider,
  getReasons,
  setStateFromPayload,
} from "../navigoSlice";
import {
  clearPricesForFamily,
  getFamilyData,
  getPriceForFamily,
} from "../../familyInfo/familySlice";
import useUnhidePageOnEnter from "../../../hooks/useUnhidePageOnEnter";
import { withFrom } from "../utils/navBack";

// Second Medical Opinion books only the Aurous plan (env-configured).
const AUROUS_PLAN_ID = process.env.REACT_APP_AUROUS_PLAN_ID;

const RELATIONS = {
  1: "Father",
  2: "Father-in-law",
  3: "Mother",
  4: "Mother-in-law",
  5: "Spouse",
  6: "Daughter (below 18 years)",
  7: "Daughter (above 18 years)",
  8: "Son (below 18 years)",
  9: "Son (above 18 years)",
  10: "Others",
};

const labelStyle = {
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  color: "#6B7280",
  marginBottom: "6px",
  display: "block",
};

const fieldStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "16px 14px",
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "15px",
  fontWeight: 500,
  backgroundColor: "#FFFFFF",
  border: "1px solid #E5E7EB",
  borderRadius: "10px",
  boxSizing: "border-box",
};

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="#97144D">
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 11.19l3.71-3.96a.75.75 0 111.08 1.04l-4.25 4.53a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

// Branded bottom-sheet select — mirrors the plan picker in navigoFirstSection8
// so we avoid the OS-native (dark) <select> popup.
function BottomSheetSelect({
  label,
  placeholder,
  value,
  displayValue,
  options,
  disabled,
  onSelect,
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: "18px" }}>
      <label style={labelStyle}>{label}</label>
      <div
        onClick={() => !disabled && setOpen(true)}
        style={{
          ...fieldStyle,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <span style={{ color: displayValue ? "#12194C" : "#9CA3AF" }}>
          {displayValue || placeholder}
        </span>
        <ChevronDown />
      </div>

      {open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-[1000] flex justify-center items-end"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full bg-white rounded-t-2xl overflow-hidden max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#E5E8F0] px-4 py-3 flex justify-between items-center border-b">
              <span className="text-sm font-bold text-[#12194C]">{label}</span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="flex items-center justify-center w-7 h-7 rounded-full text-[#12194C] hover:bg-gray-200 transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 6L18 18M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto">
              {options.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => {
                    if (opt.disabled) return;
                    onSelect(opt.value);
                    setOpen(false);
                  }}
                  className={`px-4 py-3 border-b flex items-center justify-between gap-3 ${
                    opt.disabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-[#12194C] cursor-pointer hover:bg-gray-50"
                  } ${
                    String(opt.value) === String(value)
                      ? "bg-gray-50 font-semibold"
                      : ""
                  }`}
                >
                  <div>
                    {opt.label}
                    {opt.subLabel && (
                      <span className="block text-xs text-red-600 mt-1">
                        {opt.subLabel}
                      </span>
                    )}
                  </div>
                  {opt.rightLabel && (
                    <span
                      className={`font-semibold text-sm shrink-0 ${
                        opt.disabled ? "text-gray-400" : "text-[#97144D]"
                      }`}
                    >
                      {opt.rightLabel}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SecondMedicalOpinionLanding() {
  // Ionic can leave this page `ion-page-hidden` on re-entry, blanking it.
  const pageRef = useUnhidePageOnEnter();
  const history = useHistory();
  const routeLocation = useLocation();
  const dispatch = useDispatch();
  const [isPB, setIsPB] = useState(false);

  const patientProfile = useSelector(
    (store) => store.login.user.patientProfile,
  );
  const plans = useSelector((store) => store.navigo.plans);
  const servicePlan = useSelector((store) => store.navigo.servicePlan);
  const familyData = useSelector((store) => store.familyInfo.familyData);
  const pricesForFamily = useSelector(
    (store) => store.familyInfo.pricesForFamily,
  );

  const [planId, setPlanId] = useState("");
  // "" (none) | "self" | memberPatientId(string)
  const [bookingForId, setBookingForId] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [loadingServices, setLoadingServices] = useState(false);

  // Ionic keeps this page mounted across navigation, so run on every entry
  // (not just first mount): clear any prior selections and refetch, so the
  // speciality list is always populated and no stale member stays selected.
  useIonViewWillEnter(() => {
    const source = localStorage.getItem("app_source");
    setIsPB(source === "pb");
    setBookingForId("");
    setSelectedServiceId("");
    setPlanId(""); // re-arms the getPlanService fetch below
    dispatch(getReasons("true")); // ReasonScreen reason-7 auto-select
    dispatch(getFamilyData());
    dispatch(getAllPlans({ id: patientProfile.id }));
  });

  const aurousPlan = useMemo(
    () =>
      (plans || []).find((p) => Number(p.planId) === Number(AUROUS_PLAN_ID)),
    [plans],
  );

  // Once the Aurous plan is known, fetch its services.
  useEffect(() => {
    if (aurousPlan && !planId) {
      setPlanId(aurousPlan.planId);
      setLoadingServices(true);
      dispatch(
        getPlanService({
          pageNum: 0,
          pageSize: 0,
          patientId: patientProfile.id,
          planId: aurousPlan.planId,
          serviceType: 1,
          skipValidation: true,
        }),
      ).finally(() => setLoadingServices(false));
    }
  }, [aurousPlan, planId]);

  // Speciality options = the exact same Consultation services the SMO screen
  // (navigoFirstSection8) builds from the Aurous plan.
  const finalServices = useMemo(() => {
    if (!servicePlan || servicePlan.length === 0) return [];
    return servicePlan.filter(
      (service) =>
        service?.serviceTypeName === "Consultation" &&
        service?.serviceId !== parseInt(process.env.REACT_APP_HRA_ID) &&
        service?.serviceId !== parseInt(process.env.REACT_APP_NUTRITION_ID) &&
        service?.serviceSubTypeId !== 3,
    );
  }, [servicePlan]);

  const selectedService = useMemo(
    () =>
      finalServices.find(
        (s) => String(s.serviceId) === String(selectedServiceId),
      ),
    [finalServices, selectedServiceId],
  );

  // When a speciality is chosen (and family is loaded), fetch per-member
  // eligibility/price so ineligible members can be disabled — same source the
  // old self/family picker used (getPriceForFamily -> canAvailService).
  useEffect(() => {
    if (!selectedService) return;
    const serviceMode = selectedService.serviceModeDetails?.[0]?.modeName;
    const planIdForBody = selectedService.planId || planId;

    dispatch(clearPricesForFamily());
    dispatch(
      getPriceForFamily({
        memberPatientId: patientProfile.id,
        memberRelation: 0,
        patientId: patientProfile.id,
        planId: planIdForBody,
        serviceId: selectedService.serviceId,
        serviceMode,
      }),
    );
    (familyData || []).forEach((fm) => {
      const sameRelationPIds = (familyData || [])
        .filter((member) => member.relation === fm.relation)
        .map((member) => member.memberPatientId);
      dispatch(
        getPriceForFamily({
          memberPatientId: fm.memberPatientId,
          memberRelation: fm.relation,
          patientId: fm.patientId,
          planId: planIdForBody,
          serviceId: selectedService.serviceId,
          serviceMode,
          sameRelationPIds,
        }),
      );
    });
  }, [selectedServiceId, familyData]);

  const getEligibility = (key) => {
    const smd = pricesForFamily?.[key]?.serviceModeDetails?.[0];
    return {
      canAvail: smd?.canAvailService,
      reason: smd?.nonServiceAvailabilityReason,
    };
  };

  // Per-member price for the chosen speciality (same source the old picker
  // used). Available once the getPriceForFamily fan-out resolves.
  const getPrice = (key) =>
    pricesForFamily?.[key]?.serviceModeDetails?.[0]?.discountedPrice;

  const memberLabel = (fd) =>
    `${fd.firstName}${fd.lastName ? ` ${fd.lastName}` : ""} (${
      RELATIONS[fd.relation] || "Family"
    })`;

  const selfLabel = `${patientProfile.firstName}${
    patientProfile.lastName ? ` ${patientProfile.lastName}` : ""
  } (self)`;

  // Booking-for options — ineligible members are shown but disabled once a
  // speciality has been chosen (eligibility is per-service).
  const priceLabel = (key) => {
    const p = getPrice(key);
    return p === undefined || p === null ? "" : `₹${p}`;
  };

  const bookingForOptions = useMemo(() => {
    const selfElig = getEligibility(patientProfile.id);
    const opts = [
      {
        value: "self",
        label: selfLabel,
        rightLabel: priceLabel(patientProfile.id),
        disabled: !!selectedService && selfElig.canAvail === false,
        subLabel:
          !!selectedService && selfElig.canAvail === false
            ? selfElig.reason
            : "",
      },
    ];
    (familyData || []).forEach((fd) => {
      const elig = getEligibility(fd.memberPatientId);
      opts.push({
        value: String(fd.memberPatientId),
        label: memberLabel(fd),
        rightLabel: priceLabel(fd.memberPatientId),
        disabled: !!selectedService && elig.canAvail === false,
        subLabel:
          !!selectedService && elig.canAvail === false ? elig.reason : "",
      });
    });
    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyData, selectedService, pricesForFamily, patientProfile]);

  const specialityOptions = useMemo(
    () =>
      finalServices.map((service) => ({
        value: String(service.serviceId),
        label: service.name,
      })),
    [finalServices],
  );

  const bookingForDisplay =
    bookingForId === ""
      ? ""
      : bookingForId === "self"
      ? selfLabel
      : (() => {
          const fd = (familyData || []).find(
            (f) => String(f.memberPatientId) === String(bookingForId),
          );
          return fd ? memberLabel(fd) : "";
        })();

  const selectedMemberKey =
    bookingForId === "self" ? patientProfile.id : bookingForId;
  const selectedEligibility = getEligibility(selectedMemberKey);
  const selectedIneligible =
    !!selectedService && selectedEligibility.canAvail === false;

  // Eligibility for the chosen member+speciality is resolved once its price
  // entry has arrived. Gate Continue on it so we can't proceed with a member
  // whose eligibility isn't known yet (avoids an async race).
  const eligibilityKnown =
    !!selectedService && pricesForFamily?.[selectedMemberKey] !== undefined;

  const bothChosen = bookingForId !== "" && selectedServiceId !== "";
  const canContinue = bothChosen && eligibilityKnown && !selectedIneligible;

  const handleContinue = () => {
    if (!canContinue) return;
    const isSelf = bookingForId === "self";
    const member = isSelf
      ? null
      : (familyData || []).find(
          (f) => String(f.memberPatientId) === String(bookingForId),
        );
    // Guard against a stale selection (e.g. familyData changed) so we never
    // dereference an undefined member below.
    if ((!isSelf && !member) || !selectedService) return;
    const service = {
      ...selectedService,
      planId: selectedService.planId || planId,
    };

    dispatch(
      setStateFromPayload({
        isSmoBooking: true,
        isCmpBooking: false,
        smoDraft: null,
        currentSelectedService: service,
        currentSelectedPlan: service.planId,
        currentServices: { ...service },
        familyMemberSelected: isSelf ? 0 : member.memberPatientId,
        familyMemberSelectedData: isSelf ? { ...patientProfile } : { ...member },
        isForFamily: !isSelf,
        isUserSelectedFamily: !isSelf,
      }),
    );

    // Pre-fetch providers so the provider screen (reached later) has data.
    dispatch(
      getProvider({
        cityMatch: [],
        landmarkMatch: [],
        languageMatch: [],
        pageNum: 0,
        pageSize: 0,
        patientId: patientProfile.id,
        planId: service.planId,
        serviceId: service.serviceId,
        serviceMode: service.serviceModeDetails?.[0]?.modeName,
        stateMatch: [],
      }),
    );

    // Record this screen on the way in. Without it /reasonScreen's BackButton
    // has no trail to resolve and falls through to its default "/tab/home",
    // which is not where an autologin session (module=secondMedicalOpinion)
    // came from.
    history.push(
      "/reasonScreen",
      withFrom(routeLocation, {
        services: service,
        price: 0,
        pageType: 8,
        smoCollectOnly: true,
      }),
    );
  };

  return (
    <IonPage ref={pageRef}>
      <IonContent>
        {isPB && <CommonHeader />}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100%",
            padding: "20px",
            boxSizing: "border-box",
            backgroundColor: "#FFFFFF",
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "18px",
                fontWeight: 700,
                color: "#12194C",
                letterSpacing: "-0.2px",
                margin: 0,
              }}
            >
              Second medical opinion
            </p>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={secondMedicalOpinionImg}
              alt="Second medical opinion"
              style={{ width: "100%", maxWidth: "320px", height: "auto" }}
            />
          </div>

          <p
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "15px",
              fontWeight: 400,
              lineHeight: "22px",
              color: "#4B5563",
              textAlign: "left",
              margin: "16px 0 24px",
            }}
          >
            Not sure about a diagnosis? Get a fresh perspective from another
            expert doctor so you can make confident, informed decisions about
            your health.
          </p>

          <BottomSheetSelect
            label="Select speciality"
            placeholder={loadingServices ? "Loading..." : "Select speciality"}
            value={selectedServiceId}
            displayValue={selectedService ? selectedService.name : ""}
            options={specialityOptions}
            disabled={loadingServices}
            onSelect={(v) => setSelectedServiceId(v)}
          />

          <BottomSheetSelect
            label="Booking for"
            placeholder="Select"
            value={bookingForId}
            displayValue={bookingForDisplay}
            options={bookingForOptions}
            disabled={selectedServiceId === ""}
            onSelect={(v) => setBookingForId(v)}
          />

          {selectedIneligible && selectedEligibility.reason && (
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "13px",
                color: "#DC2626",
                margin: "0 0 8px",
              }}
            >
              {selectedEligibility.reason}
            </p>
          )}

          <div style={{ flex: 1, minHeight: "24px" }} />

          <button
            onClick={handleContinue}
            disabled={!canContinue}
            style={{
              width: "100%",
              padding: "16px",
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "16px",
              fontWeight: 600,
              color: "#FFFFFF",
              backgroundColor: canContinue ? "#97144D" : "#C7B2BC",
              border: "none",
              borderRadius: "8px",
              cursor: canContinue ? "pointer" : "not-allowed",
              marginTop: "24px",
            }}
          >
            Continue
          </button>
        </div>
      </IonContent>
    </IonPage>
  );
}
